export interface FontAxis {
  tag: string;
  min: number;
  max: number;
  default: number;
  name: string;
}

export interface FontInfo {
  id: string;
  name: string;
  filename: string;
  isVariable: boolean;
  axes: FontAxis[];
  glyphCount: number;
}

export interface FontsIndex {
  fonts: FontInfo[];
}

let loadedFonts = new Map<string, boolean>();

export async function loadFont(fontInfo: FontInfo): Promise<void> {
  const fontId = fontInfo.id;

  if (loadedFonts.has(fontId)) {
    return;
  }

  const fontUrl = `/font/font/${fontInfo.filename}`;

  try {
    const fontFace = new FontFace(`FontPreview-${fontId}`, `url("${fontUrl}")`);
    await fontFace.load();
    document.fonts.add(fontFace);
    loadedFonts.set(fontId, true);
  } catch (err) {
    console.error('Failed to load font:', fontInfo.filename, err);
  }
}

export function getFontFamily(fontInfo: FontInfo): string {
  return `FontPreview-${fontInfo.id}`;
}

export function getFontVariationSettings(axisValues: Record<string, number>): string {
  return Object.entries(axisValues)
    .map(([tag, value]) => `"${tag}" ${value}`)
    .join(', ');
}

export function getAxisValue(fontInfo: FontInfo, tag: string): number {
  const axis = fontInfo.axes.find((a) => a.tag === tag);
  return axis?.default ?? 400;
}

export function downloadFont(fontInfo: FontInfo): void {
  const fontUrl = `/font/font/${fontInfo.filename}`;
  const link = document.createElement('a');
  link.href = fontUrl;
  link.download = fontInfo.filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

let opentypeFont: any = null;

export async function parseFontForGlyphs(
  fontInfo: FontInfo
): Promise<{ char: string; code: string; name: string }[]> {
  const fontUrl = `/font/font/${fontInfo.filename}`;

  try {
    const response = await fetch(fontUrl);
    const buffer = await response.arrayBuffer();

    const opentype = await import('opentype.js');
    opentypeFont = opentype.parse(buffer);

    const glyphs: { char: string; code: string; name: string }[] = [];

    for (let i = 0; i < opentypeFont.numGlyphs; i++) {
      const glyph = opentypeFont.glyphs.get(i);
      if (!glyph) continue;

      let char = '';
      if (glyph.unicode) {
        char = String.fromCodePoint(glyph.unicode);
      } else if (glyph.unicodes && glyph.unicodes.length > 0) {
        char = String.fromCodePoint(glyph.unicodes[0]);
      }

      if (char && char.charCodeAt(0) >= 32) {
        const code = glyph.unicode
          ? `U+${glyph.unicode.toString(16).toUpperCase().padStart(4, '0')}`
          : glyph.name
            ? `gid:${glyph.index}`
            : `gid:${glyph.index}`;

        glyphs.push({
          char,
          code,
          name: glyph.name || '',
        });
      }
    }

    return glyphs;
  } catch (err) {
    console.error('Failed to parse font for glyphs:', err);
    return [];
  }
}

export function formatCodepoint(glyph: { char: string; code: string }): string {
  if (glyph.char) {
    const code = glyph.char.charCodeAt(0);
    return `U+${code.toString(16).toUpperCase().padStart(4, '0')}`;
  }
  return glyph.code;
}
